<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { sortByGradeIndex } from '~/utils/gradeSorter'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { ColorName } from '~/models/Content/Color'
import { useAppColor } from '~/composables/useAppColor'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{ contents: BaseItem[] }>()
const { t } = useI18n()
const { setSelectedGrade } = useFilterStore()

const { userGradesByGradeType } = storeToRefs(useAuthStore())

const availableGrades = computed(() => {
  const grades = props.contents
    .flatMap(resource => resource.grades)
    .filter(grade => userGradesByGradeType.value.includes(grade))

  return [...new Set(grades)].sort(sortByGradeIndex)
})

const message = computed(() => {
  if (availableGrades.value.length > 0)
    return t('filters.filteredAway.messageWithGrades')
  return t('filters.filteredAway.messageWithoutGrades')
})

const { themeColor } = useAppColor({ themeColor: ColorName.Green50 })
</script>

<template>
  <div class="text-green-5 space-y-4 rounded-xl p-4" :style="{ background: themeColor }">
    <p class="text-2xl font-bold" v-text="t('filters.filteredAway.title')" />
    <p v-text="message" />

    <div
      v-if="availableGrades.length > 0"
      class="flex flex-row flex-wrap gap-4"
    >
      <Button
        v-for="grade in availableGrades"
        :key="grade"
        size="lg"
        @click="setSelectedGrade(grade)"
      >
        {{ t(`metadata.grades.${grade}`) }}
      </Button>
    </div>
  </div>
</template>
